import { groupBySection, packColumns, type MenuBlock } from '../paginate.js';
import { listColumns, listCss, listItem, type MenuTemplate, type RenderContext } from './common.js';
import {
  TG_FAMILY,
  TG_THEME,
  TG_VARIANTS,
  tgBlurb,
  tgEmblem,
  tgEmblemCss,
  tgHead,
  tgHeadCss,
  tgPanelCss,
  tgStripesCss,
  tgTitle,
} from './tangerine-split.js';

/**
 * Tangerine, layout B: the same striped panel with logo, title and blurb, and on the white side
 * the menu as a two-column list: bold capital names, dotted leaders, red prices and short
 * descriptions under accent headings. For a longer menu without photos.
 */

const S = '.mb-t-tangerine-list';
const TITLE_EM = 6.4;
const PORTRAIT_BAND = 31;

function section(ctx: RenderContext, b: MenuBlock): string {
  return `<div class="tg2-sec">${tgHead(ctx, b)}${b.items.map((i) => listItem(ctx, i, 'tg2')).join('')}</div>`;
}

export const tangerineList: MenuTemplate = {
  id: 'tangerine-list',
  name: 'Tangerine · Split and list',
  description:
    'The striped Tangerine panel with logo, huge thin title and blurb, beside the menu as a clean two-column list: bold capital names, dotted leaders and red prices. Matches the other Tangerine screens.',
  defaultTheme: TG_THEME,
  variants: TG_VARIANTS,
  cuisines: ['burgers', 'cafe', 'pizza', 'drinks'],
  family: { ...TG_FAMILY, layout: 'Split and list' },
  uses: { featured: false, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    const portrait = orientation === 'portrait';
    return packColumns(
      groupBySection(board),
      listColumns(board, {
        count: 2,
        capacity: portrait ? 120 - PORTRAIT_BAND - 6 : 67.5 - 6,
        heading: 1.7 * 1.25 + 0.6 + 1.4,
        item: { nameEm: 1.45, descEm: 0.95, perLine: portrait ? 44 : 50, gap: 1.1 },
      }),
      { balance: true },
    );
  },

  render(ctx) {
    const portrait = ctx.orientation === 'portrait';
    const cols = ctx.page.columns.map((c) => `<div class="tg2-col">${c.map((b) => section(ctx, b)).join('')}</div>`).join('');
    return [
      '<div class="tg2-panel">',
      tgEmblem(ctx, 'tg2-emblem'),
      `<div class="tg2-words">${tgTitle(ctx, 'tg2-title', portrait ? 11 : 8)}${tgBlurb(ctx, 'tg2-blurb')}</div>`,
      '</div>',
      `<div class="tg2-body">${cols}</div>`,
    ].join('');
  },

  css: `
${S}{flex-direction:row}
${S}.mb-portrait{flex-direction:column}
${tgStripesCss(S, 'tg2-panel')}
${S} .tg2-panel{flex:0 0 35%;min-width:0;display:flex;flex-direction:column;padding:3.5em 3.2em;gap:2.4em}
${S}.mb-portrait .tg2-panel{flex:0 0 ${PORTRAIT_BAND}em;flex-direction:row;align-items:center;padding:2.5em 3em}
${tgEmblemCss(S, 'tg2-emblem', 11)}
${S} .tg2-emblem{align-self:flex-start}
${S}.mb-portrait .tg2-emblem{align-self:center;flex:0 0 14em}
${S} .tg2-words{min-width:0;flex:1 1 auto;display:flex;flex-direction:column;justify-content:center}
${tgPanelCss(S, 'tg2', TITLE_EM)}
${S}.mb-portrait .tg2-title:not(.mb-2nd){font-size:5.4em}
${S}.mb-portrait .tg2-title{-webkit-line-clamp:2}
${S}.mb-portrait .tg2-blurb{-webkit-line-clamp:2;margin-top:.7em}
${S} .tg2-body{flex:1 1 auto;min-width:0;min-height:0;padding:3em;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));column-gap:3.5em}
${S} .tg2-col{min-width:0;display:flex;flex-direction:column}
${S} .tg2-sec{margin-bottom:1.5em}
${tgHeadCss(S)}
${listCss(S, 'tg2', { name: '1.45em', price: '1.45em', desc: '.95em', leader: '.16em dotted color-mix(in srgb,var(--mb-text) 35%,transparent)' })}
${S} .tg2-item{margin-bottom:1.1em}
${S} .tg2-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);text-transform:uppercase;letter-spacing:.02em;-webkit-line-clamp:1}
${S} .tg2-name.mb-ar{line-height:1.3}
${S} .tg2-price{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);color:var(--mb-accent)}
${S} .tg2-badge{background:var(--mb-primary);color:#fff}
${S} .tg2-name2{font-family:var(--mb-font-heading);font-size:1.2em;opacity:.8}
`,
};
